var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { generatePreviewList } from "healthex-common/preview.dtos";
import { ScreenshotMetadataStatus } from "healthex-common/screenshot.dtos";
import { filterNull, indexBy } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { findMatchingNamedRouteParent } from "./RoutingUIManager";
import { UserType } from "healthex-common/user.dtos";
import { StudyTranslationLanguage } from "healthex-common/study-translation-language.dtos";
import { getProjectKey, getProjectPreviewOptions } from "healthex-common/project.data.utils";
import { ProjectType } from "healthex-common/study.dtos";
export class StudyPDFPreviewUIManager {
    constructor(routingUIManager, authManager, addProjectUIManager, studiesUIManager, studyService) {
        this.routingUIManager = routingUIManager;
        this.authManager = authManager;
        this.addProjectUIManager = addProjectUIManager;
        this.studiesUIManager = studiesUIManager;
        this.studyService = studyService;
        this.screenshotsGenerating = false;
        this.pollTimeMS = 10000;
        this.pollStudyManagerTimer = null;
        reaction(() => [this.shouldPollStudyManagerStudy, this.study], () => {
            this.startPollingStudyManagerStudy();
        }, { fireImmediately: true });
        reaction(() => this.isRelevantRoute, async () => {
            if (!this.isRelevantRoute)
                this.reset();
        }, { fireImmediately: true });
    }
    get study() {
        if (this.isAddProjectRoute)
            return this.addProjectUIManager.study;
        if (this.isProjectManagerPatientExperience)
            return this.studiesUIManager.studyToManage;
        return null;
    }
    get isAddProjectRoute() {
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PROJECTS_ADD_PROJECT]);
    }
    get isProjectManagerPatientExperience() {
        var _a;
        return ((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.PROJECTS_PROJECT_MANAGER_PATIENT_EXPERIENCE.routeName;
    }
    get isRelevantRoute() {
        return this.isAddProjectRoute || this.isProjectManagerPatientExperience;
    }
    get shouldPollStudyManagerStudy() {
        var _a;
        if (!this.isProjectManagerPatientExperience)
            return false;
        if (this.study === NOT_LOADED || !((_a = this.study) === null || _a === void 0 ? void 0 : _a.previewScreenshots))
            return false;
        return this.study.previewScreenshots.some((screenshot) => screenshot.status === ScreenshotMetadataStatus.JOB_QUEUED || screenshot.status === ScreenshotMetadataStatus.IN_PROGRESS);
    }
    get includeGuided() {
        if (this.study === NOT_LOADED || !this.study)
            return false;
        return this.study.addedByOrganization.type === UserType.HEALTHCARE_ORGANIZATION;
    }
    get includeRequestTypes() {
        if (this.study === NOT_LOADED || !this.study)
            return false;
        return this.study.pdda && this.study.projectType === ProjectType.GENERAL_DATA_USE && !!this.study.healthRecordsUpdatesEnabled;
    }
    get generatableLanguages() {
        if (this.study === NOT_LOADED || !this.study)
            return [];
        return filterNull(this.study.studyTranslatableFields.map((fields) => fields.language === StudyTranslationLanguage.en || (fields.enabled && fields.completed) ? fields.language : null));
    }
    get previewScreenshots() {
        var _a;
        if (this.study === NOT_LOADED || !this.study)
            return [];
        const previewScreenshotsByKey = indexBy((_a = this.study.previewScreenshots) !== null && _a !== void 0 ? _a : [], (screenshot) => screenshot.key);
        const languages = filterNull(this.study.studyTranslatableFields.map((fields) => (fields.enabled ? fields.language : null)));
        return generatePreviewList({
            languages,
            includeGuided: this.includeGuided,
            includeRequestTypes: this.includeRequestTypes,
        }).map((previewMetadata) => {
            const generatedScreenshot = previewScreenshotsByKey[previewMetadata.key];
            if (generatedScreenshot) {
                return generatedScreenshot;
            }
            else {
                return {
                    ...previewMetadata,
                    status: "NOT_GENERATED",
                    disableGeneration: !this.generatableLanguages.includes(previewMetadata.language),
                };
            }
        });
    }
    startPollingStudyManagerStudy() {
        this.clearPollingStudyManagerStudy();
        if (!this.shouldPollStudyManagerStudy)
            return;
        this.pollStudyManagerTimer = window.setTimeout(async () => {
            if (this.shouldPollStudyManagerStudy) {
                await this.refreshStudy();
                this.startPollingStudyManagerStudy();
            }
        }, this.pollTimeMS);
    }
    clearPollingStudyManagerStudy() {
        if (this.pollStudyManagerTimer !== null)
            window.clearTimeout(this.pollStudyManagerTimer);
    }
    async refreshStudyAndRestartPolling() {
        if (this.isAddProjectRoute) {
            this.addProjectUIManager.clearPollStudyTimer();
            await this.refreshStudy();
            this.addProjectUIManager.pollStudy();
        }
        else {
            this.clearPollingStudyManagerStudy();
            await this.refreshStudy();
            this.startPollingStudyManagerStudy();
        }
    }
    async refreshStudy() {
        if (this.isAddProjectRoute)
            await this.addProjectUIManager.refreshStudy({ preserveDirty: true });
        if (this.isProjectManagerPatientExperience)
            await this.studiesUIManager.refreshStudyToManage();
    }
    async generateScreenshots(metadatas) {
        if (this.study === NOT_LOADED || !this.study || !this.authManager.orgId)
            return;
        if (this.screenshotsGenerating)
            return;
        this.screenshotsGenerating = true;
        try {
            await this.refreshStudy();
            await this.studyService.generateOrDownloadInformedConsent(this.study.id, this.authManager.orgId, this.study.studyVersionId, metadatas);
            await this.refreshStudyAndRestartPolling();
        }
        finally {
            this.screenshotsGenerating = false;
        }
    }
    reset() {
        this.screenshotsGenerating = false;
    }
    get projectKeyParams() {
        if (this.study === NOT_LOADED || this.study === null)
            return null;
        const { projectType, studyType, accessData, pdda } = this.study;
        return { projectType, studyType, accessData, pdda };
    }
    get projectKey() {
        if (this.projectKeyParams === null)
            return null;
        const { projectType, studyType, accessData, pdda } = this.projectKeyParams;
        try {
            return getProjectKey(projectType, studyType, accessData, pdda);
        }
        catch (e) {
            return null;
        }
    }
    get projectPreviewOptions() {
        return getProjectPreviewOptions(this.projectKey);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], StudyPDFPreviewUIManager.prototype, "screenshotsGenerating", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "study", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "isAddProjectRoute", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "isProjectManagerPatientExperience", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "isRelevantRoute", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "shouldPollStudyManagerStudy", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "includeGuided", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "includeRequestTypes", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "generatableLanguages", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "previewScreenshots", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "projectKeyParams", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "projectKey", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyPDFPreviewUIManager.prototype, "projectPreviewOptions", null);
